import { useState, useRef, useMemo } from "react";
import { faTimes, faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Checkbox from "@mui/material/Checkbox";
import { Tooltip } from "@mui/material";

import Alert from "components/Common/Alert/Alert";
import Button from "components/Common/Button/Button";
import ButtonSmall from "components/Common/ButtonSmall/ButtonSmall";
import TextField from "components/Common/TextField/TextField";
import * as Database from "utils/database.utils";
import { useSettings } from "context/Settings.context";

import CompanyModalContainer from "../CompanyModal.styles";
import CompanyForm, {
    CompanyFormRef,
    initialFormData,
    useBbgEditableFields,
    useBbgFetch,
} from "../CompanyForm";

type Props = {
    setShowModal: Function;
    reloadCompanies?: Function;
};

export default function CreateCompanyModal({
    setShowModal,
    reloadCompanies,
}: Props) {
    const { setToast } = useSettings();
    const formRef = useRef<CompanyFormRef>(null);

    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1);
    const [showConfirmClose, setShowConfirmClose] = useState(false);
    const [ticker, setTicker] = useState("");
    const [ipoFlag, setIpoFlag] = useState(false);
    const [tickerError, setTickerError] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const bbg = useBbgFetch();
    const bbgEditable = useBbgEditableFields();

    const formInitialData = useMemo(
        () => ({
            ...initialFormData,
            ticker: ticker,
            ipo_flag: ipoFlag,
        }),
        [ticker, ipoFlag]
    );

    const handleClose = () => {
        if (step === 2) {
            setShowConfirmClose(true);
        } else {
            setShowModal(false);
        }
    };

    const normalizeTicker = (tickerValue: string): string => {
        const parts = tickerValue.trim().split(/\s+/);
        if (parts.length < 2) return tickerValue.toUpperCase();
        const lastPart = parts.pop()!;
        const normalizedLast =
            lastPart.charAt(0).toUpperCase() + lastPart.slice(1).toLowerCase();
        return [...parts.map((p) => p.toUpperCase()), normalizedLast].join(" ");
    };

    const handleNext = () => {
        setTickerError(null);

        if (ipoFlag) {
            bbg.reset();
            bbgEditable.enableAll();
            formRef.current?.updateFormData({
                ticker: ticker,
                ipo_flag: true,
            });
            setStep(2);
            return;
        }

        const normalizedTicker = normalizeTicker(ticker);
        setTicker(normalizedTicker);

        bbg.setStatus("loading");

        const apiCall = Database.post("api/company/bbg-details", {
            ticker: normalizedTicker,
        });

        const timeout = new Promise<"timeout">((resolve) =>
            setTimeout(() => resolve("timeout"), 1000)
        );

        Promise.race([apiCall, timeout])
            .then(async (result) => {
                if (result === "timeout") {
                    formRef.current?.updateFormData({
                        ticker: normalizedTicker,
                        ipo_flag: false,
                    });
                    setStep(2);
                    const res = await apiCall;
                    if (res.ok) {
                        const data = await res.json();
                        formRef.current?.updateFormData({
                            name: data.name,
                            isin: data.isin,
                            sedol: data.sedol,
                            cusip: data.cusip,
                            eu_composite_ticker: data.eu_composite_ticker,
                        });
                        bbg.setStatus("success");
                    } else {
                        bbg.setStatus("error");
                    }
                } else {
                    const res = result;
                    if (res.ok) {
                        const data = await res.json();
                        formRef.current?.updateFormData({
                            ticker: normalizedTicker,
                            ipo_flag: false,
                            name: data.name,
                            isin: data.isin,
                            sedol: data.sedol,
                            cusip: data.cusip,
                            eu_composite_ticker: data.eu_composite_ticker,
                        });
                        bbg.setStatus("success");
                        setStep(2);
                    } else {
                        const errorData = await res.json();
                        let errorMessage = "Failed to fetch from Bloomberg";
                        if (typeof errorData.error === "string") {
                            errorMessage = errorData.error;
                        } else if (errorData.error?.ticker?.[0]) {
                            errorMessage = errorData.error.ticker[0];
                        }
                        setTickerError(errorMessage);
                        bbg.reset();
                    }
                }
            })
            .catch(() => {
                setTickerError("Failed to connect to Bloomberg");
                bbg.reset();
            });
    };

    const retryBbgFetch = async () => {
        const currentTicker = formRef.current?.getFormData().ticker || ticker;
        const result = await bbg.fetch(currentTicker);

        if (result.success) {
            formRef.current?.updateFormData(result.data);
            bbgEditable.disableAll();
        }
    };

    const handleSave = async () => {
        if (!formRef.current) return;

        setLoading(true);
        setFieldErrors({});

        const errors = formRef.current.validate();

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            setLoading(false);
            return;
        }

        try {
            const data = await formRef.current.prepareSubmitData();
            const res = await Database.post("api/company", data);

            if (res.ok) {
                setToast({
                    show: true,
                    type: "success",
                    message: "Company created successfully",
                });
                reloadCompanies?.();
                setShowModal(false);
            } else {
                const errorResponse = await res.json();
                if (
                    errorResponse.error &&
                    typeof errorResponse.error === "object"
                ) {
                    const newErrors: Record<string, string> = {};
                    Object.entries(errorResponse.error).forEach(
                        ([field, messages]) => {
                            if (
                                Array.isArray(messages) &&
                                messages.length > 0
                            ) {
                                newErrors[field] = messages[0] as string;
                            }
                        }
                    );
                    setFieldErrors(newErrors);
                } else {
                    setToast({
                        show: true,
                        type: "error",
                        message:
                            errorResponse.error || "Failed to create company",
                    });
                }
            }
        } catch (err) {
            setToast({
                show: true,
                type: "error",
                message: "An error occurred while creating the company",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <CompanyModalContainer>
            <div className={"modalInner"}>
                <div className={"header"}>
                    <div className={"topRow"}>
                        <div className={"title"}>
                            <h1>ADD NEW COMPANY</h1>
                        </div>
                        <div className={"headerActions"}>
                            <ButtonSmall
                                text={"CLOSE"}
                                color={"third"}
                                icon={faTimes}
                                click={handleClose}
                            />
                        </div>
                    </div>
                </div>

                <div className={"content noGap"}>
                    {step === 1 ? (
                        <div className={"stepOneContainer"}>
                            <div className={"checkboxField labelFirst"}>
                                <Checkbox
                                    checked={ipoFlag}
                                    onChange={(e) =>
                                        setIpoFlag(e.target.checked)
                                    }
                                    disableRipple
                                />
                                <label>IPO</label>
                            </div>
                            <p className="stepOneDescription">
                                {ipoFlag ? (
                                    <>Enter a placeholder ticker for the IPO.</>
                                ) : (
                                    <>
                                        Enter the full ticker including exchange
                                        and "Equity" suffix.
                                        <br />
                                        This will be used to fetch company name,
                                        ISIN, CUSIP, and SEDOL from Bloomberg.
                                    </>
                                )}
                            </p>
                            <div className={"tickerField"}>
                                <label>
                                    Ticker{" "}
                                    <Tooltip
                                        placement="top"
                                        title={
                                            <span>
                                                Main exchange codes:
                                                <br />
                                                US, NA, JP, GY, HK, TT, SW, LN,
                                                IN, SS, IJ, CH, DC, AU, FP, FH,
                                                ID, KS, SP
                                            </span>
                                        }
                                    >
                                        <FontAwesomeIcon
                                            icon={faQuestionCircle}
                                        />
                                    </Tooltip>
                                </label>
                                <TextField
                                    value={ticker}
                                    change={(val: string) => {
                                        setTicker(val);
                                        setTickerError(null);
                                    }}
                                    placeholder={"e.g. AAPL US Equity"}
                                    error={!!tickerError}
                                    errorText={tickerError || undefined}
                                />
                            </div>
                        </div>
                    ) : (
                        <CompanyForm
                            ref={formRef}
                            initialData={formInitialData}
                            showBbgFields={!ipoFlag}
                            hideIpoCheckbox
                            bbgStatus={bbg.status}
                            bbgEditableFields={bbgEditable.fields}
                            onEnableBbgField={bbgEditable.enableField}
                            onRetryBbgFetch={retryBbgFetch}
                            fieldErrors={fieldErrors}
                            setFieldErrors={setFieldErrors}
                        />
                    )}
                </div>

                <div className={"actions"}>
                    {step === 1 ? (
                        <Button
                            text={
                                bbg.status === "loading" ? "LOADING..." : "NEXT"
                            }
                            color={"primary"}
                            click={handleNext}
                            disabled={
                                !ticker.trim() || bbg.status === "loading"
                            }
                        />
                    ) : (
                        <Button
                            text={loading ? "SAVING..." : "SAVE"}
                            color={"primary"}
                            click={handleSave}
                            disabled={loading}
                        />
                    )}
                </div>
            </div>

            {showConfirmClose && (
                <Alert>
                    <h2>ARE YOU SURE?</h2>
                    <p>Progress will be lost.</p>
                    <Button
                        text="YES, CLOSE"
                        color="accent"
                        click={() => setShowModal(false)}
                    />
                    <Button
                        text="CANCEL"
                        color="flat"
                        click={() => setShowConfirmClose(false)}
                    />
                </Alert>
            )}
        </CompanyModalContainer>
    );
}
